<script setup lang="ts">
	useHead({
		meta: [
			{ name: 'geo.placename', content: 'Anton-Zickmantel-Straße 50, 04249 Leipzig, Germany'},
			{ name: 'geo.position', content: '51.303287,12.316606'},
			{ name: 'geo.region', content: 'DE-SN'},
			{ name: 'theme-color', content: '#003462'},
			{ name: 'msapplication-TileColor', content: '#003462'},
			{ property: 'og:type', content: 'website'},
			{ property: 'og:locale', content: 'de_DE'}
		],
		link: [
			{ rel: 'icon', type: 'image/png', sizes: '16x16', content: usePublicUrl('/icons/favicon-16x16.png')},
			{ rel: 'icon', type: 'image/png', sizes: '32x32', content: usePublicUrl('/icons/favicon-32x32.png')},
			{ rel: 'icon', type: 'image/png', sizes: '192x192', content: usePublicUrl('/icons/android-chrome-192x192.png')},
			{ rel: 'icon', type: 'image/png', sizes: '512x512', content: usePublicUrl('/icons/android-chrome-512x512.png')},
			{ rel: 'apple-touch-icon', content: usePublicUrl('/icons/apple-touch-icon.png')},
			{ rel: 'msapplication-TileImage', content: usePublicUrl('/icons/tileImage-144x144.png')},
			{ rel: 'manifest', content: usePublicUrl('/icons/site.webmanifest')},
			{ rel: 'describedby', href: '/llms.txt'}
		]
	})

	const menu = [
		{label: 'Start', to: {name: 'home'}},
		{label: 'Standorte', to: {name: 'article-detail', params: {article_slug: 'standorte'}}},
		{label: 'Geschäftsfelder', to: {name: 'article-detail', params: {article_slug: 'geschaeftsfelder'}}},
		{label: 'Karriere', to: {name: 'article-detail', params: {article_slug: 'karriere'}}},
		{
			label: 'Über uns',
			type: 'dropdown',
			children: [
				{label: 'Unser Leitbild', to: {name: 'article-detail', params: {article_slug: 'unser-leitbild'}}},
				{label: 'Nachhaltigkeit', to: {name: 'article-detail', params: {article_slug: 'nachhaltigkeit'}}},
				// {label: 'Downloads', to: {name: 'article-detail', params: {article_slug: 'downloads'}}},
				{label: 'Zertifikate', to: {name: 'article-detail', params: {article_slug: 'unsere-zertifikate-und-nachweise'}}},
				{label: 'Management', to: {name: 'article-detail', params: {article_slug: 'management'}}}
			]
		},
		{label: 'Aktuelles', to: {name: 'article-detail', params: {article_slug: 'aktuelles'}}},
		{label: 'Kontakt', to: {name: 'article-detail', params: {article_slug: 'kontakt'}}}
	]

	const { data: profile, status: profileStatus } = useLazyDataResolver('profile')

	const route = useRoute()
	const headerHasBackground = computed(() => {
		// array contains all pages that should have a background
		return route.path === '/' || route.path === '/karriere' || route.path === '/stellenangebote' || route.path === '/stellenangebote-uns' || route.path === '/haeufige-fragen'
	})

	const isIntranetPage = computed(() => {
		return route.path.startsWith('/intranet') && !route.path.endsWith('/login')
	})
</script>

<template>
	<GsApp color-mode-preference="light">
		<NuxtLayout name="site-default" sticky>
			<template #header>
				<GsPageHeader v-if="!isIntranetPage"
					:style="{ '--ui-header-shadow': headerHasBackground ? 'none' : '0px 4px 25px 0px rgba(225,226,229,1.00)' }"
					:resize-on-scroll="{ height: '200px'}"
					:toggle="{
						variant: 'solid',
						size: 'xl',
						class: 'w-12 h-12 lg:w-16 lg:h-16 aspect-square flex [&_.iconify]:m-auto rounded-full text-white bg-primary hover:bg-accent hover:text-white cursor-pointer duration-300 transition-all',
						ui: { leadingIcon: 'size-6 lg:size-9'}
					}"
					:ui="{
						root: 'border-none bg-transparent backdrop-blur-none data-small:shadow-none data-small:bg-transparent data-small:[--ui-header-shadow:0px_4px_20px_0px_rgba(225,226,229,0.8)]! [--ui-logo-padding:0.25rem] md:[--ui-logo-padding:0.375rem] xl:[--ui-logo-padding:0.45rem] data-small:[--ui-logo-background:white] data-small:[--ui-dropdown-nav-top:90px]',
						container: 'lg:[[data-small=true]_&]:h-[120px]',
						center: 'transition-all duration-200',
						right: 'transition-all duration-200 h-full flex-0!',
						left: 'items-top'
					}"
					:nav-bar="{
						items: menu,
						slot: 'center',
						horizontal: {
							variant: 'link',
							color: 'primary',
							ui: {
								root: 'hidden lg:inline-block my-auto bg-white rounded-full shadow-[var(--ui-header-shadow)] px-3 py-1',
								list: 'lg:gap-1.5 xl:gap-2.5 [&_li:last-child]:ps-2.5 2xl:[&_li:last-child]:ps-5 [&_li:last-child>a]:bg-primary [&_li:last-child>a:hover]:bg-accent [&_li:last-child>a]:text-white [&_li:last-child>a]:rounded-full',
								link: 'px-2.5 2xl:px-5 py-2.5 xl:py-3.5 group-data-small:py-1.5 text-[1.25rem] lg:text-[1.125rem] xl:text-[1.25rem] text-black',
								linkLabel: 'hover:underline decoration-accent decoration-2 underline-offset-12 hover:underline-offset-6 transition-all duration-300',
								viewportWrapper: 'top-[var(--ui-dropdown-nav-top)] w-60 left-[50%] rounded-3xl overflow-hidden shadow-[0px_4px_25px_0px_rgba(0,0,0,0.15)]',
								content: 'bg-white',
								childList: 'grid-cols-1 gap-0',
								childLink: 'text-[1.125rem] text-black leading-9 tracking-wide hover:text-black hover:bg-white data-active:bg-white hover:underline data-active:underline decoration-accent decoration-2 underline-offset-12 hover:underline-offset-6 data-active:underline-offset-6 transition-all duration-300'
							}
						},
						vertical: {
							variant: 'link',
							color: 'primary',
							ui: {
								list: 'space-y-1.5 [&>li:last-child>a]:bg-primary [&>li:last-child>a:hover]:bg-accent [&>li:last-child>a]:text-white [&>li:last-child>a]:rounded-full [&>li:last-child>a>span]:mx-auto [&>li:last-child]:my-6',
								link: 'bg-alto/20 text-[1.25rem] sm:text-[1.375rem] md:text-[1.5rem] text-black hover:text-accent',
								childList: 'border-none mt-2.5',
								childItem: 'border-b-2 border-white'
							}
						}
					}"
				>
					<template #title>
						<div class="p-(--ui-logo-padding) rounded-b-xl bg-(--ui-logo-background) transition duration-200 ease-in-out">
							<GsImg
								:src="usePublicUrl('~/assets/img/Logo-Ludwig-Pfeiffer.svg')"
								class="h-auto md:[[data-small=true]_&]:h-[65px] md:h-[75px] lg:[[data-small=true]_&]:h-[100px] lg:h-[125px] w-auto relative"
								fit="contain"
								alt="Logo der Ludwig Pfeiffer Hoch- und Tiefbau GmbH & Co. KG"
								width="128" height="114"
							/>
						</div>
					</template>

					<template #right>
						<NuxtLink :to="{ name: 'intranet:dashboard' }" title="Interner Bereich" :class="['text-[1rem] hidden lg:inline-flex *:my-auto  hover:text-accent transition-all duration-300 ease-in-out', headerHasBackground ? 'text-white/60' : 'text-primary/40']" no-prefetch>
							<GsIcon name="mdi-lock-outline" class="size-10 transition duration-300 ease-in-out"></GsIcon>
							<span class="sr-only">Intranet</span>
						</NuxtLink>
					</template>

					<template #body-append>
						<NuxtLink :to="{ name: 'intranet:dashboard' }" title="Interner Bereich" class="flex gap-2 items-center justify-center w-full px-4 py-1.5 bg-gray-400 hover:bg-accent text-white text-[1.25rem] font-medium rounded-full mx-auto transition-all duration-300 ease-in-out" no-prefetch>
							<GsIcon name="mdi-lock-outline" class="size-8 transition duration-300 ease-in-out"></GsIcon>
							Intranet
						</NuxtLink>
					</template>

				</GsPageHeader>
			</template>

			<NuxtPage />

		</NuxtLayout>
	</GsApp>
</template>
